/**
 * Notes Display Component
 * Shows all notes for a session with user attribution
 * Allows users to edit/delete their own notes
 */

import React, { useState } from 'react';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Icon from '@amzn/meridian/icon';
import documentTokens from "@amzn/meridian-tokens/base/icon/document"
import { useAppSelector, useAppDispatch } from '../../../store/store';
import { saveNote } from '../../../store/sessionSlice';
import { showSuccess, showError } from '../../../store/toastsSlice';
import { formatISOTimestampToDateTime } from '../../../utils/dateUtil';
import { TEXT_STYLE_BODY, TEXT_STYLE_LINK, TEXT_STYLE_SMALL, TEXT_COLOR_SECONDARY, FONT_SIZE_BODY, FONT_SIZE_EMPTY_STATE_ICON, LINE_HEIGHT_BODY } from '../cssConstants';
import { useFontSize } from './FontSizeContext';

interface NotesDisplayProps {
  sessionId: string;
}

const NotesDisplay: React.FC<NotesDisplayProps> = ({ sessionId }) => {
  const { fontSize } = useFontSize();
  const notes = useAppSelector((state) => state.session.notes);
  const currentUser = useAppSelector((state) => state.user.alias);
  const { saveNoteStatus } = useAppSelector((state) => state.session);
  const dispatch = useAppDispatch();
  
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const isSaving = saveNoteStatus === 'pending';

  const handleEdit = (noteId: string, content: string) => {
    setEditingNoteId(noteId);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditContent('');
  };

  const handleUpdate = async () => {
    if (!editingNoteId || !editContent.trim()) return;
    try {
      await dispatch(saveNote({
        sessionId,
        content: editContent.trim(),
        noteId: editingNoteId,
      })).unwrap();
      dispatch(showSuccess('Note updated'));
      setEditingNoteId(null);
      setEditContent('');
    } catch (error: any) {
      dispatch(showError(error || 'Failed to update note'));
    }
  };

  // Sort notes by creation time (newest first)
  const sortedNotes = [...notes].sort((a, b) => 
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  if (notes.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        color: TEXT_COLOR_SECONDARY,
      }}>
        <div style={{ fontSize: FONT_SIZE_EMPTY_STATE_ICON, marginBottom: '16px' }}><Icon tokens={documentTokens} color="primary" /></div>
        <Text color="secondary">
          No notes yet. Create your first note below.
        </Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {sortedNotes.map((note) => {
        const isOwnNote = note.created_by === currentUser;
        const isEditing = editingNoteId === note.note_id;

        return (
          <div key={note.note_id} style={{ padding: '16px 0', borderBottom: '1px solid #e0e0e0' }}>
            {/* Header: author, date, action buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={TEXT_STYLE_LINK}>
                  {note.created_by}
                </span>
                <span style={TEXT_STYLE_SMALL}>
                  {formatISOTimestampToDateTime(note.created_at)}
                  {note.updated_at !== note.created_at && ' (edited)'}
                </span>
              </div>
              {isOwnNote && (
                isEditing ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="small" type="link" onClick={handleCancelEdit} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="small" type="link" onClick={handleUpdate} disabled={isSaving || !editContent.trim()}>
                      {isSaving ? 'Saving...' : 'Update'}
                    </Button>
                  </div>
                ) : (
                  <Button size="small" type="link" onClick={() => handleEdit(note.note_id, note.content)}>
                    Edit
                  </Button>
                )
              )}
            </div>
            {/* Note content or edit textarea */}
            {isEditing ? (
              <textarea
                value={editContent}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditContent(e.target.value)}
                rows={4}
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  fontSize: fontSize,
                  lineHeight: LINE_HEIGHT_BODY,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              <div style={{ 
                ...TEXT_STYLE_BODY,
                fontSize: fontSize,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {note.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default NotesDisplay;
